import React, { useState, useEffect } from 'react';
import { collection, query, where, getDocs, doc, updateDoc, orderBy, addDoc } from 'firebase/firestore';
import { db } from '../../services/firebase';
import { Bug, Lightbulb, Clock, CheckCircle, XCircle, ChevronDown, ChevronUp, ExternalLink, Monitor, User, Calendar, MessageSquare, Filter } from 'lucide-react';
import { formatDate } from '../../utils/helpers';

const FeedbackManagement = () => {
    const [feedback, setFeedback] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState('all'); // all, bug, feature
    const [statusFilter, setStatusFilter] = useState('all'); // all, new, in-progress, resolved, closed
    const [expandedId, setExpandedId] = useState(null);
    const [updatingId, setUpdatingId] = useState(null);

    useEffect(() => {
        loadFeedback();
    }, []);

    const loadFeedback = async () => {
        try {
            setLoading(true);
            const feedbackRef = collection(db, 'feedback');
            // Fetch all feedback and filter client-side to avoid index requirements
            const snapshot = await getDocs(feedbackRef);
            const feedbackData = snapshot.docs
                .map(doc => ({
                    id: doc.id,
                    ...doc.data()
                }))
                // Filter for bug reports and feature requests only
                .filter(f => f.type === 'bug' || f.type === 'feature')
                // Sort by createdAt descending
                .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));

            setFeedback(feedbackData);
            console.log('✅ Loaded', feedbackData.length, 'feedback items');
        } catch (error) {
            console.error('Error loading feedback:', error);
        } finally {
            setLoading(false);
        }
    };

    const updateStatus = async (feedbackId, newStatus) => {
        try {
            setUpdatingId(feedbackId);
            const feedbackItem = feedback.find(f => f.id === feedbackId);
            const feedbackRef = doc(db, 'feedback', feedbackId);
            const updateData = {
                status: newStatus
            };

            if (newStatus === 'resolved' || newStatus === 'closed') {
                updateData.resolvedAt = new Date().toISOString();
            }

            await updateDoc(feedbackRef, updateData);

            // Send notification to user when feedback is resolved
            if (newStatus === 'resolved' && feedbackItem?.userId) {
                try {
                    const notificationsRef = collection(db, 'notifications');
                    await addDoc(notificationsRef, {
                        userId: feedbackItem.userId,
                        type: 'feedback_resolved',
                        message: feedbackItem.type === 'bug'
                            ? `🐛 Your bug report has been resolved: "${feedbackItem.description.substring(0, 50)}${feedbackItem.description.length > 50 ? '...' : ''}"`
                            : `💡 Your feature request has been addressed: "${feedbackItem.description.substring(0, 50)}${feedbackItem.description.length > 50 ? '...' : ''}"`,
                        feedbackType: feedbackItem.type,
                        feedbackId: feedbackId,
                        read: false,
                        createdAt: new Date().toISOString()
                    });
                    console.log('✅ Notification sent to user');
                } catch (notifError) {
                    console.error('Error sending notification:', notifError);
                }
            }

            // Update local state
            setFeedback(prev => prev.map(f =>
                f.id === feedbackId ? { ...f, ...updateData } : f
            ));
        } catch (error) {
            console.error('Error updating feedback status:', error);
            alert('Failed to update status');
        } finally {
            setUpdatingId(null);
        }
    };

    const addAdminNote = async (feedbackId, note) => {
        if (!note.trim()) return;

        try {
            const feedbackRef = doc(db, 'feedback', feedbackId);
            const existing = feedback.find(f => f.id === feedbackId);
            const existingNotes = existing?.adminNotes || [];

            const newNote = {
                note: note.trim(),
                addedAt: new Date().toISOString()
            };

            await updateDoc(feedbackRef, {
                adminNotes: [...existingNotes, newNote]
            });

            // Update local state
            setFeedback(prev => prev.map(f =>
                f.id === feedbackId ? { ...f, adminNotes: [...existingNotes, newNote] } : f
            ));
        } catch (error) {
            console.error('Error adding note:', error);
        }
    };

    const getTypeIcon = (type) => {
        switch (type) {
            case 'bug': return <Bug className="text-red-500" size={20} />;
            case 'feature': return <Lightbulb className="text-yellow-500" size={20} />;
            default: return <MessageSquare className="text-blue-500" size={20} />;
        }
    };

    const getTypeLabel = (type) => {
        switch (type) {
            case 'bug': return 'Bug Report';
            case 'feature': return 'Feature Request';
            default: return 'Feedback';
        }
    };

    const getStatusBadge = (status) => {
        switch (status) {
            case 'new':
                return <span className="px-2 py-1 bg-blue-100 text-blue-700 text-xs rounded-full font-medium">New</span>;
            case 'in-progress':
                return <span className="px-2 py-1 bg-yellow-100 text-yellow-700 text-xs rounded-full font-medium">In Progress</span>;
            case 'resolved':
                return <span className="px-2 py-1 bg-green-100 text-green-700 text-xs rounded-full font-medium">Resolved</span>;
            case 'closed':
                return <span className="px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded-full font-medium">Closed</span>;
            default:
                return <span className="px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded-full font-medium">{status}</span>;
        }
    };

    const filteredFeedback = feedback.filter(f => {
        if (filter !== 'all' && f.type !== filter) return false;
        if (statusFilter !== 'all' && f.status !== statusFilter) return false;
        return true;
    });

    const stats = {
        total: feedback.length,
        bugs: feedback.filter(f => f.type === 'bug').length,
        features: feedback.filter(f => f.type === 'feature').length,
        new: feedback.filter(f => f.status === 'new').length,
        inProgress: feedback.filter(f => f.status === 'in-progress').length
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Stats Cards */}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                <div className="bg-white rounded-xl shadow-sm p-4">
                    <p className="text-sm text-gray-500">Total</p>
                    <p className="text-2xl font-bold text-gray-800">{stats.total}</p>
                </div>
                <div className="bg-white rounded-xl shadow-sm p-4">
                    <p className="text-sm text-gray-500 flex items-center gap-1">
                        <Bug size={14} className="text-red-500" /> Bugs
                    </p>
                    <p className="text-2xl font-bold text-red-600">{stats.bugs}</p>
                </div>
                <div className="bg-white rounded-xl shadow-sm p-4">
                    <p className="text-sm text-gray-500 flex items-center gap-1">
                        <Lightbulb size={14} className="text-yellow-500" /> Features
                    </p>
                    <p className="text-2xl font-bold text-yellow-600">{stats.features}</p>
                </div>
                <div className="bg-white rounded-xl shadow-sm p-4">
                    <p className="text-sm text-gray-500">New</p>
                    <p className="text-2xl font-bold text-blue-600">{stats.new}</p>
                </div>
                <div className="bg-white rounded-xl shadow-sm p-4">
                    <p className="text-sm text-gray-500">In Progress</p>
                    <p className="text-2xl font-bold text-yellow-600">{stats.inProgress}</p>
                </div>
            </div>

            {/* Filters */}
            <div className="bg-white rounded-xl shadow-sm p-4">
                <div className="flex flex-wrap items-center gap-4">
                    <div className="flex items-center gap-2">
                        <Filter size={18} className="text-gray-500" />
                        <span className="text-sm font-medium text-gray-700">Filters:</span>
                    </div>

                    <select
                        value={filter}
                        onChange={(e) => setFilter(e.target.value)}
                        className="px-3 py-1.5 border rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
                    >
                        <option value="all">All Types</option>
                        <option value="bug">Bug Reports</option>
                        <option value="feature">Feature Requests</option>
                    </select>

                    <select
                        value={statusFilter}
                        onChange={(e) => setStatusFilter(e.target.value)}
                        className="px-3 py-1.5 border rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
                    >
                        <option value="all">All Status</option>
                        <option value="new">New</option>
                        <option value="in-progress">In Progress</option>
                        <option value="resolved">Resolved</option>
                        <option value="closed">Closed</option>
                    </select>

                    <button
                        onClick={loadFeedback}
                        className="ml-auto px-3 py-1.5 text-sm text-blue-600 hover:bg-blue-50 rounded-lg"
                    >
                        Refresh
                    </button>
                </div>
            </div>

            {/* Feedback List */}
            {filteredFeedback.length === 0 ? (
                <div className="bg-white rounded-xl shadow-sm p-12 text-center">
                    <MessageSquare className="mx-auto text-gray-300 mb-4" size={48} />
                    <p className="text-gray-500">No feedback found</p>
                </div>
            ) : (
                <div className="space-y-4">
                    {filteredFeedback.map((item) => (
                        <div key={item.id} className="bg-white rounded-xl shadow-sm overflow-hidden">
                            {/* Header - Always Visible */}
                            <div
                                onClick={() => setExpandedId(expandedId === item.id ? null : item.id)}
                                className="p-4 cursor-pointer hover:bg-gray-50 transition-colors"
                            >
                                <div className="flex items-start gap-4">
                                    <div className="mt-1">{getTypeIcon(item.type)}</div>

                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-2 flex-wrap">
                                            <span className={`text-xs font-medium px-2 py-0.5 rounded ${item.type === 'bug' ? 'bg-red-100 text-red-700' : 'bg-yellow-100 text-yellow-700'
                                                }`}>
                                                {getTypeLabel(item.type)}
                                            </span>
                                            {getStatusBadge(item.status)}
                                        </div>

                                        <p className="font-medium text-gray-800 mt-2 line-clamp-2">
                                            {item.description}
                                        </p>

                                        <div className="flex items-center gap-4 mt-2 text-xs text-gray-500">
                                            <span className="flex items-center gap-1">
                                                <User size={12} />
                                                {item.userName || item.userEmail}
                                            </span>
                                            <span className="flex items-center gap-1">
                                                <Calendar size={12} />
                                                {formatDate(item.createdAt)}
                                            </span>
                                            <span className="flex items-center gap-1">
                                                <Monitor size={12} />
                                                {item.currentPage}
                                            </span>
                                        </div>
                                    </div>

                                    <div className="flex-shrink-0">
                                        {expandedId === item.id ? (
                                            <ChevronUp size={20} className="text-gray-400" />
                                        ) : (
                                            <ChevronDown size={20} className="text-gray-400" />
                                        )}
                                    </div>
                                </div>
                            </div>

                            {/* Expanded Content */}
                            {expandedId === item.id && (
                                <div className="border-t px-4 py-4 bg-gray-50 space-y-4">
                                    {/* Full Description */}
                                    <div>
                                        <h4 className="text-sm font-semibold text-gray-700 mb-2">Description</h4>
                                        <p className="text-gray-600 whitespace-pre-wrap bg-white p-3 rounded-lg border">
                                            {item.description}
                                        </p>
                                    </div>

                                    {/* Screenshot */}
                                    {item.screenshotURL && (
                                        <div>
                                            <h4 className="text-sm font-semibold text-gray-700 mb-2">Screenshot</h4>
                                            <a
                                                href={item.screenshotURL}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="inline-block"
                                            >
                                                <img
                                                    src={item.screenshotURL}
                                                    alt="Screenshot"
                                                    className="max-w-md rounded-lg border shadow-sm hover:shadow-md transition-shadow"
                                                />
                                            </a>
                                        </div>
                                    )}

                                    {/* Technical Details */}
                                    <div>
                                        <h4 className="text-sm font-semibold text-gray-700 mb-2">Technical Details</h4>
                                        <div className="bg-white p-3 rounded-lg border text-xs text-gray-600 space-y-1">
                                            <p><strong>User:</strong> {item.userEmail}</p>
                                            <p><strong>Page:</strong> {item.currentPage}</p>
                                            <p><strong>Screen:</strong> {item.screenSize}</p>
                                            <p><strong>Browser:</strong> {item.userAgent?.substring(0, 100)}...</p>
                                            <p><strong>Submitted:</strong> {formatDate(item.createdAt, { hour: 'numeric', minute: '2-digit' })}</p>
                                        </div>
                                    </div>

                                    {/* Admin Notes */}
                                    <div>
                                        <h4 className="text-sm font-semibold text-gray-700 mb-2">Admin Notes</h4>
                                        {item.adminNotes && item.adminNotes.length > 0 ? (
                                            <div className="space-y-2 mb-3">
                                                {item.adminNotes.map((note, idx) => (
                                                    <div key={idx} className="bg-white p-3 rounded-lg border text-sm">
                                                        <p className="text-gray-700">{note.note}</p>
                                                        <p className="text-xs text-gray-400 mt-1">
                                                            {formatDate(note.addedAt, { hour: 'numeric', minute: '2-digit' })}
                                                        </p>
                                                    </div>
                                                ))}
                                            </div>
                                        ) : (
                                            <p className="text-sm text-gray-400 mb-3">No notes yet</p>
                                        )}

                                        <form
                                            onSubmit={(e) => {
                                                e.preventDefault();
                                                const input = e.target.elements.note;
                                                addAdminNote(item.id, input.value);
                                                input.value = '';
                                            }}
                                            className="flex gap-2"
                                        >
                                            <input
                                                type="text"
                                                name="note"
                                                placeholder="Add a note..."
                                                className="flex-1 px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
                                            />
                                            <button
                                                type="submit"
                                                className="px-4 py-2 bg-gray-600 text-white rounded-lg text-sm hover:bg-gray-700"
                                            >
                                                Add Note
                                            </button>
                                        </form>
                                    </div>

                                    {/* Status Actions */}
                                    <div>
                                        <h4 className="text-sm font-semibold text-gray-700 mb-2">Update Status</h4>
                                        <div className="flex flex-wrap gap-2">
                                            {['new', 'in-progress', 'resolved', 'closed'].map(status => (
                                                <button
                                                    key={status}
                                                    onClick={() => updateStatus(item.id, status)}
                                                    disabled={item.status === status || updatingId === item.id}
                                                    className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${item.status === status
                                                        ? 'bg-blue-600 text-white'
                                                        : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                                                        } disabled:opacity-50`}
                                                >
                                                    {status === 'in-progress' ? 'In Progress' : status.charAt(0).toUpperCase() + status.slice(1)}
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

export default FeedbackManagement;